import { z } from 'zod'

import type { RequestService } from '../request'
import type { ScalarConfig } from '@scalar-org/scalar-config'
import type { Project } from '@scalar-org/entities/project'
import type { Theme } from '@scalar-org/entities/theme'

export function publishApiFactory(requestService: RequestService) {
  /** Check DNS record setup for a domain */
  async function checkDNSRecords(domain: string) {
    return await requestService.request({
      url: 'core/publish/dns-check',
      method: 'post',
      data: {
        domain,
      },
      schema: z.object({
        invalidRecords: z.boolean(),
      }),
    })
  }

  /** Deploy */
  async function deploy(projectUid: string, versions?: string[]) {
    return await requestService.request({
      url: 'core/publish',
      method: 'post',
      data: {
        projectUid,
        versions,
      },
      schema: z.null(),
    })
  }

  /**
   * This flow is available only for github projects that are not linked.
   *
   * It will deploy a new build for the specified github project
   *
   */
  async function deployCli(data: {
    config: ScalarConfig
    /** Formatted project that shares the git project uid */
    project: Project
    /** page uids to content strings */
    documents: Record<string, string>
    /** reference uids to content strings */
    references: Record<string, string>
    theme: Theme
  }) {
    return await requestService.request({
      url: 'core/publish/cli',
      method: 'post',
      data,
      schema: z.object({ publishUid: z.string() }),
    })
  }

  /** Unpublish a deployed site */
  async function unpublish(projectUid?: string, githubProjectUid?: string) {
    return await requestService.request({
      url: 'core/unpublish',
      method: 'delete',
      data: {
        projectUid,
        githubProjectUid,
      },
      schema: z.null(),
    })
  }

  /** Re-deploy a publish record */
  async function redeploy(publishUid: string) {
    return await requestService.request({
      url: 'core/publish/redeploy',
      method: 'post',
      data: {
        publishUid,
      },
      schema: z.null(),
    })
  }

  /** Delete a deploy record */
  async function deleteRecord(publishUid: string) {
    return await requestService.request({
      url: 'core/publish',
      method: 'delete',
      data: {
        publishUid,
      },
      schema: z.null(),
    })
  }

  async function bulkDelete(projectUid: string) {
    return await requestService.request({
      url: 'core/publish/bulk',
      method: 'delete',
      data: {
        projectUid,
      },
      schema: z.null(),
    })
  }

  return {
    checkDNSRecords,
    deploy,
    deployCli,
    unpublish,
    deleteRecord,
    redeploy,
    bulkDelete,
  }
}
